import { useState } from 'react';
import { toast } from 'react-toastify';
import { cancelarTramitePorAmbito, reactivarTramitePorAmbito } from '../../services/tramiteService';

const MOTIVOS = [
    'Solicitud del cliente',
    'Documentación incompleta',
    'Cambio de circunstancias',
    'Error en la gestión',
    'Desistimiento voluntario',
    'Otros',
];

/**
 * Botón "Cancelar Trámite" / "Reactivar Trámite" para los módulos que no lo tenían (Reajuste,
 * Jubilación, Pensión, Ganancias, Minusvalía, Declaratoria, Plan de Pago, Reconocimiento,
 * Amparo PNC, PNC, PUAM). Mismo look & feel y misma trazabilidad que el de RTI/ART (motivo +
 * observaciones quedan en el historial de estado), pero pega contra /cancelar-ambito y
 * /reactivar-ambito: esos resuelven el "Cancelado" del PROPIO ámbito del trámite, no un id fijo
 * (ver TramiteService.cancelarTramitePorAmbito). No reemplaza la cancelación que cada módulo ya
 * tiene disponible desde la línea de estados: es una vía más, con el mismo resultado.
 *
 * `tramite` solo necesita `idTramite` y `nombreEstadoActual`. `onActualizado` recibe el DTO ya
 * actualizado para que la ficha lo aplique con setTramite (mismo patrón que ChecklistTramite).
 */
export default function BotonCancelarReactivarTramite({ tramite, onActualizado }) {
    const [dialogo, setDialogo] = useState(null); // null | 'CANCELAR' | 'REACTIVAR'
    const [motivo, setMotivo] = useState('');
    const [observaciones, setObservaciones] = useState('');
    const [enviando, setEnviando] = useState(false);

    if (!tramite) return null;

    const estaCancelado = (tramite.nombreEstadoActual || '').toLowerCase().includes('cancelado');

    const cerrarDialogo = () => {
        setDialogo(null);
        setMotivo('');
        setObservaciones('');
    };

    const handleCancelar = async () => {
        setEnviando(true);
        try {
            const actualizado = await cancelarTramitePorAmbito(tramite.idTramite, motivo, observaciones);
            cerrarDialogo();
            onActualizado?.(actualizado);
            toast.success('Trámite cancelado correctamente');
        } catch (error) {
            toast.error(error.response?.data || 'Error al cancelar el trámite');
        } finally {
            setEnviando(false);
        }
    };

    const handleReactivar = async () => {
        setEnviando(true);
        try {
            const actualizado = await reactivarTramitePorAmbito(tramite.idTramite, observaciones);
            cerrarDialogo();
            onActualizado?.(actualizado);
            toast.success('Trámite reactivado correctamente');
        } catch (error) {
            toast.error(error.response?.data || 'Error al reactivar el trámite');
        } finally {
            setEnviando(false);
        }
    };

    return (
        <>
            {!estaCancelado && (
                <button
                    className="btn btn-outline-danger d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                    style={{ fontSize: '1rem' }}
                    onClick={() => setDialogo('CANCELAR')}
                >
                    <i className="bi bi-slash-circle-fill"></i> Cancelar Trámite
                </button>
            )}
            {estaCancelado && (
                <button
                    className="btn btn-outline-success d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                    style={{ fontSize: '1rem' }}
                    onClick={() => setDialogo('REACTIVAR')}
                >
                    <i className="bi bi-arrow-counterclockwise"></i> Reactivar Trámite
                </button>
            )}

            {dialogo === 'CANCELAR' && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content shadow-lg border-0 rounded-4">
                            <div className="modal-header border-0 pb-2">
                                <h5 className="modal-title fw-bold text-warning">
                                    <i className="bi bi-slash-circle-fill me-2"></i>Cancelar Trámite
                                </h5>
                                <button type="button" className="btn-close" onClick={cerrarDialogo}></button>
                            </div>
                            <div className="modal-body px-4">
                                <div className="alert alert-warning d-flex align-items-center mb-3">
                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                    <div>
                                        <strong>¿Está seguro de cancelar este trámite?</strong><br />
                                        <small>El trámite pasará al estado "Cancelado". Esta acción se registrará en el historial.</small>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Motivo de cancelación <span className="text-danger">*</span>:</label>
                                    <select className="form-select shadow-sm" value={motivo} onChange={(e) => setMotivo(e.target.value)} required>
                                        <option value="">Seleccione un motivo...</option>
                                        {MOTIVOS.map((m) => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Observaciones (opcional):</label>
                                    <textarea className="form-control shadow-sm" rows="3" placeholder="Detalles adicionales..."
                                        value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
                                </div>
                            </div>
                            <div className="modal-footer border-0 pt-0">
                                <button type="button" className="btn btn-secondary rounded-pill px-4" onClick={cerrarDialogo} disabled={enviando}>Volver</button>
                                <button type="button" className="btn btn-warning rounded-pill px-4 fw-semibold" onClick={handleCancelar} disabled={!motivo.trim() || enviando}>
                                    <i className="bi bi-slash-circle-fill me-1"></i> Cancelar Trámite
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {dialogo === 'REACTIVAR' && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content rounded-4 shadow-lg border-0">
                            <div className="modal-header border-0 pb-0">
                                <h5 className="modal-title fw-bold text-success">
                                    <i className="bi bi-arrow-counterclockwise me-2"></i>Reactivar Trámite
                                </h5>
                                <button type="button" className="btn-close" onClick={cerrarDialogo}></button>
                            </div>
                            <div className="modal-body py-4">
                                <div className="alert alert-info border-0 rounded-3">
                                    <strong>Información:</strong>
                                    <p className="mb-0 mt-2">El trámite será reactivado y volverá al estado anterior a la cancelación.<br />Esta acción se registrará en el historial.</p>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold text-muted">Observaciones (obligatorio):</label>
                                    <textarea className="form-control rounded-3 border-2" placeholder="Explique el motivo de la reactivación..."
                                        value={observaciones} onChange={(e) => setObservaciones(e.target.value)} rows="4"
                                        style={{ resize: 'vertical', minHeight: '100px' }} />
                                    <div className="form-text">Mínimo 5 caracteres requeridos</div>
                                </div>
                            </div>
                            <div className="modal-footer border-0 pt-0">
                                <button type="button" className="btn btn-secondary rounded-pill px-4" onClick={cerrarDialogo} disabled={enviando}>Volver</button>
                                <button type="button" className="btn btn-success rounded-pill px-4 fw-semibold" onClick={handleReactivar}
                                    disabled={!observaciones.trim() || observaciones.trim().length < 5 || enviando}>
                                    <i className="bi bi-arrow-counterclockwise me-1"></i> Reactivar Trámite
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
